/**
 * 流式会话纯函数（原 useStreamSession.ts 中的模块级函数）
 *
 * 包含用户消息构造、附件上传与引用处理等纯函数。
 */
import { API_BASE } from '@/config';
import { Message, AttachmentItem } from '../types';

export function buildUserMessage(input: string, attachments: AttachmentItem[], references: any[]): Message {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    role: 'user',
    text: input,
    attachments: attachments.length > 0 ? attachments : undefined,
    references: references.length > 0
      ? references.map((r: any) => ({ id: r.id, thumbnailUrl: r.thumbnailUrl, title: r.node.title }))
      : undefined,
  };
}

/**
 * 上传图片文件到后端 temp-upload 接口，返回本地 /assets/ 路径。
 * 由后端负责压缩、分段等预处理，前端不再做任何缩放。
 * projectId 用于按用户/画布隔离存储路径。
 */
async function uploadImage(file: File, projectId?: string): Promise<string | null> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const url = projectId
      ? `${API_BASE}/api/temp-upload?projectId=${encodeURIComponent(projectId)}`
      : `${API_BASE}/api/temp-upload`;
    const res = await fetch(url, { method: 'POST', body: formData });
    if (!res.ok) return null;
    const data = await res.json();
    return data.url || null;
  } catch {
    return null;
  }
}

export async function processAttachments(attachments: AttachmentItem[], references: any[], projectId?: string) {
  const attachmentPayloads = await Promise.all(
    attachments.map(async (a) => {
      const base: any = {
        name: a.name,
        type: a.type,
        size: a.size,
        thumbnailUrl: a.thumbnailUrl,
      };
      if (a.file && a.type.startsWith('image/')) {
        const assetPath = await uploadImage(a.file, projectId);
        if (assetPath) {
          base.dataUrl = assetPath;
        }
      }
      return base;
    })
  );

  const referencePayloads = references.map((r: any) => {
    const nodeData = r.node.data || {};
    const rawUrl = nodeData.image || nodeData.videoUri || r.thumbnailUrl || '';
    let dataUrl = '';
    let imageUrl = '';
    if (rawUrl.startsWith('data:')) {
      dataUrl = rawUrl;
      imageUrl = rawUrl;
    } else if (rawUrl.startsWith('http')) {
      imageUrl = rawUrl;
    } else if (rawUrl.startsWith('/assets/') || rawUrl.startsWith('/api/')) {
      imageUrl = `${API_BASE}${rawUrl}`;
    }
    return {
      id: r.id,
      title: r.node.title,
      thumbnailUrl: imageUrl || r.thumbnailUrl,
      nodeType: r.node.type,
      dataUrl,
    };
  });

  return { attachmentPayloads, referencePayloads };
}
